import { Slide, useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import articlePlaceholder from '../../../assets/articlePlaceholder.png';
import { useThemeContext } from '../../../global-state/themeContext';
import { ProductDetails, SoldProductItem } from '../../../hooks';
import { Chip, Drawer, Icon, Icons, SkeletonLoader, Typography } from '../../../stories/atoms';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../stories/atoms/containerV2/ContainerV2';
import { Animation, Animations, DynamicTab, TabLabelProps } from '../../../stories/molecules';
import { Typographies, muiTheme } from '../../../theme';
import { soldProductsTranslations } from '../../../translations';
import { Spacing } from '../../../utils';
import { getArticleAssetUrl } from '../../../utils/soldProducts';
import { StyledIcon } from '../../home/common/widgetCardContent/WidgetCardContent.styles';
import {
  SliderWrapper,
  StyledAboutInfo,
  StyledColor,
  StyledDetailsProperty,
  StyledProductImage,
  StyledProductSmallImage,
} from './ProductDetailsSlider.styles';

const { productDetailsView, departmentId, season, salesNet, piecesSold, productId } = soldProductsTranslations;

const imageAssetTypes = ['descriptiveStillLife', 'descriptiveDetail', 'lookbook'] as const;

export type ProductDetailsSliderProps = {
  isVisible: boolean;
  product: SoldProductItem;
  productDetails?: ProductDetails | null;
  onClose: () => void;
  isDetailsLoading?: boolean;
  isDetailsError?: boolean;
};

type ImageProps = {
  src: string;
  isSelected?: boolean;
  onClick?: () => void;
  loading: boolean;
  setLoading: (value: boolean) => void;
  articlePlaceholder: string;
};

const ProductImage = ({ src, isSelected, onClick, loading, setLoading }: ImageProps) => (
  <StyledProductSmallImage
    data-testid="small-product-image"
    src={src}
    onLoadStart={() => setLoading(true)}
    onLoad={() => setLoading(false)}
    alt=""
    isSelected={isSelected}
    onClick={onClick}
    onError={(e) => {
      const target = e.target as HTMLImageElement;
      target.src = articlePlaceholder;
    }}
    style={{ display: loading ? 'none' : 'auto' }}
  />
);

const ImageSkeleton = ({ height }: { height: string }) => {
  return (
    <ContainerV2 height="54vh" margin={[3, 8]} borderRadius={[3]} data-testid="skeleton-image">
      <SkeletonLoader showRoundBorders variant="rect" width="100%" height={height} animationType="pulse" />
    </ContainerV2>
  );
};

export const ProductDetailsSlider: FC<React.PropsWithChildren<ProductDetailsSliderProps>> = observer(
  ({ isVisible, onClose, product, productDetails, isDetailsLoading, isDetailsError }) => {
    const [shouldOpen, setShouldOpen] = useState(isVisible);
    const [tabIndex, setTabIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    const { t } = useTranslation();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
    const { mode } = useThemeContext();

    useEffect(() => {
      setShouldOpen(isVisible);
    }, [isVisible]);

    const imageUrls = useMemo(() => {
      return imageAssetTypes.map((type) =>
        getArticleAssetUrl({
          articleId: `${product.productId}${product.mostSoldArticle}`,
          assetType: type,
          rendition: 'large',
        }),
      );
    }, [product]);

    const getTabs = (): TabLabelProps[] => {
      return [
        {
          tabName: t(productDetailsView.about.key, productDetailsView.about.defaultValue),
          isTabVisible: true,
          labelType: 'body3' as Typographies,
        },
        {
          tabName: t(productDetailsView.details.key, productDetailsView.details.defaultValue),
          isTabVisible: true,
          labelType: 'body3' as Typographies,
          labelMargin: [0] as Spacing,
        },
        {
          tabName: t(productDetailsView.materials.key, productDetailsView.materials.defaultValue),
          isTabVisible: true,
          labelType: 'body3' as Typographies,
          labelMargin: [0] as Spacing,
          isTabDisabled: true,
        },
      ];
    };

    const handleTabChange = (event: React.SyntheticEvent, newTabIndex: number) => {
      setTabIndex(newTabIndex);
    };

    const handleClose = () => {
      setShouldOpen(false);
      if (onClose) onClose();
    };

    if (!product) {
      return null; // Render nothing if product details are not available
    }

    useEffect(() => {
      setLoading(true);
      setSelectedImage(null);
    }, [product.productId]);

    const productProperties = [
      {
        label: t(productDetailsView.department.key, productDetailsView.department.defaultValue),
        value: product.departmentName,
      },
      { label: t(departmentId.key, departmentId.defaultValue), value: product.departmentNumber },
      { label: t(season.key, season.defaultValue), value: product.season },
      { label: t(piecesSold.key, piecesSold.defaultValue), value: product.soldPieces },
      { label: t(salesNet.key, salesNet.defaultValue), value: product.salesNet.toFixed(2) },
    ];

    // TODO: Move to separate component
    const AboutTabView = () => {
      return (
        <div data-testid="aboutView">
          {loading && <ImageSkeleton height="54vh" />}
          <StyledProductImage
            data-testid="product-image"
            src={selectedImage || imageUrls[0]}
            onLoadStart={() => setLoading(true)}
            onLoad={() => {
              setLoading(false);
              if (!selectedImage) {
                setSelectedImage(imageUrls[0]);
              }
            }}
            alt=""
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = articlePlaceholder;
              setLoading(false);
            }}
            style={{ display: loading ? 'none' : 'auto' }}
          />
          {!loading && (
            <RowFlexContainer justifyContent="around" margin={[0, 7]}>
              {imageUrls.map((imgUrl, idx) => (
                <ProductImage
                  key={idx}
                  src={imgUrl}
                  data-testid="small-product-image"
                  isSelected={selectedImage === imgUrl}
                  onClick={() => {
                    setLoading(true);
                    setSelectedImage(imgUrl);
                  }}
                  loading={loading}
                  setLoading={setLoading}
                  articlePlaceholder={articlePlaceholder}
                />
              ))}
            </RowFlexContainer>
          )}
          <StyledAboutInfo>
            <div>
              <Typography type="body3" margin={[0, 0, 2, 0]}>
                {product.productName}
              </Typography>
              <Typography type="body3">{product.price}</Typography>
            </div>

            <Typography textAlign="left" color="textGray" type="legal">
              {t(productId.key, productId.defaultValue)}: {product.productId}
            </Typography>
          </StyledAboutInfo>

          <ContainerV2 data-testid="details-card" margin={[5]} padding={[3]} borderRadius={[3]} shadow>
            {productProperties.map((property, index) => (
              <StyledDetailsProperty key={index}>
                <Typography>{property.label}</Typography>
                <Typography>{property.value}</Typography>
              </StyledDetailsProperty>
            ))}
          </ContainerV2>
        </div>
      );
    };

    // TODO: Move to separate component
    const SizeColorTabView = () => {
      if (isDetailsError && !productDetails) {
        return (
          <Animation
            animation={Animations.SAD}
            size="small"
            title=""
            subtitle={t(
              soldProductsTranslations.detailsError.key,
              soldProductsTranslations.detailsError.defaultValue,
            )}
          />
        );
      }
      const colors = productDetails?.color ?? [];
      const sizes = productDetails?.size ?? [];

      return (
        <div data-testid="size-color-view">
          {isDetailsLoading && <ImageSkeleton height="24vh" />}
          {!isDetailsLoading && productDetails && (
            <ColumnFlexContainer padding={[4]}>
              <RowFlexContainer data-testid="color-row" margin={[2, 0]} alignItems="baseline" gap={[2]}>
                <RowFlexContainer alignItems="center" gap={[2]}>
                  <StyledIcon data-testid="color-icon" icon={Icons.COLOR_PICKER} size="small" />
                  <Typography>{t(productDetailsView.color.key, productDetailsView.color.defaultValue)}</Typography>
                </RowFlexContainer>
                <RowFlexContainer data-testid="colors" flex={1} flexWrap="wrap" gap={[1]} justifyContent="end">
                  {colors.map((color) => (
                    <StyledColor key={color} color={color} />
                  ))}
                </RowFlexContainer>
              </RowFlexContainer>
              <RowFlexContainer data-testid="size-row" margin={[2, 0]} alignItems="baseline" gap={[2]}>
                <RowFlexContainer alignItems="center" gap={[2]}>
                  <StyledIcon data-testid="closeIcon" icon={Icons.MEASURE} size="small" />
                  <Typography>{t(productDetailsView.size.key, productDetailsView.size.defaultValue)}</Typography>
                </RowFlexContainer>
                <RowFlexContainer data-testid="sizes" flex={1} flexWrap="wrap" justifyContent="end" gap={[1]}>
                  {sizes.map((size) => (
                    <Chip
                      key={size}
                      label={size}
                      color="primary"
                      variant="filled"
                      size="small"
                      borderRadius="0px"
                    />
                  ))}
                </RowFlexContainer>
              </RowFlexContainer>
            </ColumnFlexContainer>
          )}
        </div>
      );
    };

    const ProductDetailsView = () => {
      return (
        <SliderWrapper>
          <RowFlexContainer alignItems="center" justifyContent="between" padding={[5, 5, 0, 5]}>
            <div>
              <Typography type="h3">
                {t(productDetailsView.title.key, productDetailsView.title.defaultValue)}
              </Typography>
              {/* <StyledSubheaderWrapper>
                <Badge backgroundColor={stockAvailable ? 'successLight' : 'errorLight'}>
                  <Typography type="body3" color={stockAvailable ? 'validationGreen' : 'red'}>
                    {stockAvailable
                      ? t(inStock.key, inStock.defaultValue)
                      : t(outOfStock.key, outOfStock.defaultValue)}
                  </Typography>
                </Badge>
                <Typography>{remainingPieces} pcs left</Typography>
              </StyledSubheaderWrapper> */}
            </div>
            <Icon data-testid="closeIcon" icon={Icons.CLOSE} onClick={handleClose} />
          </RowFlexContainer>

          <ContainerV2
            data-testid="product-details-view"
            height="100%"
            padding={[1]}
            textAlign="center"
            overflow="auto"
          >
            <DynamicTab
              tabLabelArray={getTabs()}
              handleTabChange={handleTabChange}
              tabValue={tabIndex}
              variant="scrollable"
              tabBgColor={mode === 'light' ? 'white' : 'inherit'}
              content={[
                {
                  tabContent: <AboutTabView />,
                  visible: true,
                },
                {
                  tabContent: <SizeColorTabView />,
                  visible: true,
                },
              ]}
            />
          </ContainerV2>
        </SliderWrapper>
      );
    };

    return (
      <>
        {isMobile ? (
          <Drawer
            variant="temporary"
            position="right"
            isOpen={shouldOpen}
            onClose={handleClose}
            drawerWidth={muiTheme.spacing(90)}
          >
            <ProductDetailsView />
          </Drawer>
        ) : (
          <Slide
            in={shouldOpen}
            direction="left"
            timeout={300}
            data-testid="slider-view"
            mountOnEnter
            unmountOnExit
            style={{ height: '100%' }}
          >
            <div>
              <ProductDetailsView />
            </div>
          </Slide>
        )}
      </>
    );
  },
);
